import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import {
    PollQuestion as Question,
    PollRecordedAnswer,
    PollRecordedQuestion,
} from '@calm/evexia-web-sdk';
import programMessages from '@rhg/lang/messages/program';
import {
    Box,
    IconButton,
    SvgIcon,
    SxProps,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { StarFilled, StarOutline } from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const styleQuestionContainer = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};
const styleDescriptionContainer = {
    mb: DS_SPACING_UNITS.CONTENT,
};
const styleRangeContainer = {
    width: 'fit-content',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};
const styleRangeLabelContainer = {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
};

const styleNpsChoice = {
    '& > svg': {
        width: '32px',
        height: '32px',
    },
};

const styleIcon = { width: '32px', height: '32px' };
const styleFilled = {
    color: 'secondary.main',
};
const styleOutline = {
    color: 'text.sub',
};

interface PollQuestionProps {
    question: Question;
    variant?: 'nps';
    showDescription?: boolean;
    sx?: SxProps<Theme>;
    onAnswer?: (answer: PollRecordedQuestion) => void;
}
export function PollQuestion({
    question,
    variant,
    showDescription = true,
    sx,
    onAnswer,
}: PollQuestionProps) {
    const [focusedOption, setFocusedOption] = useState<number | undefined>(
        undefined
    );
    const [hasAnswered, setHasAnswered] = useState(false);
    const rangeArray = useMemo(() => {
        if (
            question?.range?.min === undefined ||
            question?.range?.max === undefined
        ) {
            return [];
        } else {
            const diff = question?.range?.max - question?.range?.min + 1;
            return Array.from(
                { length: diff },
                (_, i) => question.range!.min! + i
            );
        }
    }, [question.range]);

    const handleAnswer = useCallback(
        (answer: PollRecordedAnswer) => {
            onAnswer?.({ questionId: question.id, answer });
        },
        [onAnswer, question.id]
    );

    const handleRangeAnswer = useCallback(
        (choice: number) => {
            if (!hasAnswered) {
                setHasAnswered(true);
                setFocusedOption(choice);
                handleAnswer({ range: choice });
            }
        },
        [handleAnswer, hasAnswered]
    );

    const setFocus = useCallback(
        (choice?: number) => {
            if (!hasAnswered) {
                setFocusedOption(choice);
            }
        },
        [hasAnswered]
    );

    return (
        <Box sx={sxAppend(styleQuestionContainer, sx)}>
            {showDescription && (
                <Box sx={styleDescriptionContainer}>
                    <Typography
                        variant="subtitle1"
                        component="p"
                        color="text.sub"
                        textAlign="center"
                    >
                        {question.title}
                    </Typography>
                    {question?.subtitle && (
                        <Typography
                            mt={2}
                            variant="subtitle1"
                            component="p"
                            color="text.sub"
                            textAlign="center"
                        >
                            {question.subtitle}
                        </Typography>
                    )}
                </Box>
            )}
            {question.questionType === 'range' && question?.range && (
                <Box sx={styleRangeContainer}>
                    {question.range?.minLabel && question.range?.maxLabel && (
                        <Box sx={styleRangeLabelContainer}>
                            <Typography
                                variant="subtitle2"
                                component="p"
                                color="text.body"
                                textAlign="center"
                                mr={DS_SPACING_UNITS.CONTENT}
                            >
                                {question.range?.minLabel}
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                component="p"
                                color="text.body"
                                textAlign="center"
                            >
                                {question.range?.maxLabel}
                            </Typography>
                        </Box>
                    )}
                    {variant === 'nps' ? (
                        <Box>
                            {rangeArray.map((i) => (
                                <IconButton
                                    trackButton="NpsChoice"
                                    sx={styleNpsChoice}
                                    key={i}
                                    onClick={() => handleRangeAnswer(i)}
                                    onFocusVisible={() => setFocus(i)}
                                    onMouseEnter={() => setFocus(i)}
                                    onMouseLeave={() => setFocus(undefined)}
                                    aria-label={i}
                                >
                                    <SvgIcon sx={styleIcon}>
                                        {focusedOption && focusedOption >= i ? (
                                            <StarFilled
                                                sx={styleFilled}
                                                data-testid="StarIcon"
                                            />
                                        ) : (
                                            <StarOutline
                                                sx={styleOutline}
                                                data-testid="StarOutlineIcon"
                                            />
                                        )}
                                    </SvgIcon>
                                </IconButton>
                            ))}
                        </Box>
                    ) : (
                        <Typography>TODO, regular range questions</Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}

interface PollQuestionResponseProps {
    question: Question;
    answer?: PollRecordedQuestion['answer'];
    sx?: SxProps<Theme>;
}
export function PollQuestionResponse({
    question,
    answer,
    sx,
}: PollQuestionResponseProps) {
    const { formatMessage } = useIntl();
    const rangeArray = useMemo(() => {
        if (
            question?.range?.min === undefined ||
            question?.range?.max === undefined
        ) {
            return [];
        } else {
            const diff = question?.range?.max - question?.range?.min + 1;
            return Array.from(
                { length: diff },
                (_, i) => question.range!.min! + i
            );
        }
    }, [question.range]);

    if (
        question.questionType !== 'range' ||
        !question?.range ||
        !answer?.range
    ) {
        return null;
    }

    return (
        <Box
            sx={sxAppend(styleQuestionContainer, sx)}
            role="group"
            aria-label={formatMessage(
                programMessages['program.program_completed.rating_label'],
                { count: answer.range }
            )}
        >
            <Box sx={styleRangeContainer}>
                <Box>
                    {rangeArray.map((i) => (
                        <SvgIcon
                            sx={sxAppend(styleNpsChoice, styleIcon)}
                            key={i}
                        >
                            {answer?.range && answer.range >= i ? (
                                <StarFilled
                                    sx={styleFilled}
                                    data-testid="StarIcon"
                                />
                            ) : (
                                <StarOutline
                                    sx={styleOutline}
                                    data-testid="StarOutlineIcon"
                                />
                            )}
                        </SvgIcon>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
